import type { BuildingDefinition } from "@common/definitions/buildings";
import { interiorRectangles, permanentInteriorWalls, tiledInteriorGeometry } from "@common/utils/interiorGeometry";
import { FloorNames } from "@common/utils/terrain";
import { Container, Graphics, Mesh, MeshGeometry } from "pixi.js";
import { PIXI_SCALE } from "./constants";
import { paintedInteriorMaterials } from "./stylizedEnvironment";
import { vaultInterior } from "@common/utils/vaultInterior";

const geometryCache = new Map<string, { material: string, tint: number, geometry: MeshGeometry }[]>();

/** Small shared painted tiles preserve detail at game zoom without allocating enormous floor textures. */
export function createPaintedInterior(definition: BuildingDefinition): Container | undefined {
    definition = vaultInterior(definition) ?? definition;
    if (!definition.paintedInterior || !paintedInteriorMaterials.size) return undefined;
    let surfaces = geometryCache.get(definition.idString);
    if (!surfaces) {
        surfaces = [];
        for (const [type, material, tint] of [
            [FloorNames.Stone, "momo_ceramic_floor_tile", 0xffffff],
            [FloorNames.Wood, "momo_oak_floor_tile", 0xffffff],
            [FloorNames.Metal, "momo_industrial_floor_tile", 0xffffff],
            [FloorNames.Carpet, "momo_industrial_floor_tile", 0x648b83]
        ] as const) {
            const regions = (definition.floors ?? []).filter(floor => floor.type === type && !floor.layer)
                .flatMap(floor => interiorRectangles(floor.hitbox));
            if (!regions.length) continue;
            surfaces.push({ material, tint, geometry: new MeshGeometry(tiledInteriorGeometry(regions, PIXI_SCALE)) });
        }
        geometryCache.set(definition.idString, surfaces);
    }
    const container = new Container();
    for (const { material, tint, geometry } of surfaces) {
        const texture = paintedInteriorMaterials.get(material);
        if (!texture) continue;
        container.addChild(new Mesh({ geometry, texture, tint }));
    }
    const walls = new Graphics();
    for (const { min, max } of permanentInteriorWalls(definition.hitbox)) {
        const x = min.x * PIXI_SCALE, y = min.y * PIXI_SCALE;
        const w = (max.x - min.x) * PIXI_SCALE, h = (max.y - min.y) * PIXI_SCALE;
        walls.rect(x + 7, y + 8, w, h).fill({ color: 0x243b40, alpha: 0.3 });
        walls.rect(x, y, w, h).fill(0x263e46);
        walls.rect(x + 4, y + 4, Math.max(1, w - 8), Math.max(1, h - 8)).fill(0xb5c1b1);
        walls.rect(x + 4, y + 4, Math.max(1, w - 8), Math.min(5, h - 8)).fill(0xe6e5cf);
    }
    // Wall tops render above the floor images/entrance props but below actors and loot.
    walls.zIndex = 1;
    container.sortableChildren = true;
    container.addChild(walls);
    return container;
}

export function replacesInteriorImage(key: string): boolean {
    return /^headquarters_(?:floor_(?:top|bottom|entrance)|second_floor_(?:top|bottom))$/.test(key);
}
